// Kumadori Club (world.json `club`): the Nishi tower block as a walled open-roof nightclub. This file registers the
// wall-segment kind/roof the scene uses for the solid ring and paints everything inside it (LED dance floor, DJ stage
// with a screen wall, two bars, the VIP lounge, doors, signs) plus the baked lights. Live beams and the animated floor
// sit on top (hd-club-fx.ts). Art only: every collision tile is defined in world.json.
import { WORLD } from '../../common/src/data';
import { T } from '../../common/src/map';
import { paintNishiClubWall } from './hd-nishi-refresh';
import { hash2 } from '../../common/src/util';
import type { Rect } from '../../common/src/data';
import { type G, K, HT, px, frame, shadow, disc, dk, lt, mix, alpha, speckle, text, textW } from './hd-kit';
import { registerFront, registerRoof, type LightFn } from './hd-registry';

/** wall segments carry no front of their own: paintClub draws the whole ring over them */
registerFront('clubWall', { upper: 'none' }, () => { /* painted by paintClub */ });
registerRoof('clubWall', { parapet: false, paint: (g, x, y, w, h) => { px(g, x, y, w, h, K.ink); px(g, x, y, w, 3, K.metalLo); } });

const inR = (r: Rect, tx: number, ty: number) => tx >= r.x && ty >= r.y && tx < r.x + r.w && ty < r.y + r.h;
/** is this tile part of the club block (walls and floor)? the scene skips its planter/tree art there */
export const isClubTile = (tx: number, ty: number) => inR(WORLD.club.block, tx, ty);

const RED = K.red, GOLD = K.warmHi;

/** kabuki kumadori face paint: white face, red lines sweeping from the eyes, black brows, red lips */
function kumadori(g: G, cx: number, cy: number, s: number, glow: string = K.pink) {
  disc(g, cx, cy, 8 * s, 10 * s, K.ink); disc(g, cx, cy, 7 * s, 9 * s, K.white);
  for (const d of [-1, 1]) {
    for (let i = 0; i < 6; i++) px(g, cx + d * (2 + i) * s - (d < 0 ? s : 0), cy - (3 - i * 0.5) * s, s, 2 * s, RED);   // sweeping brow lines
    for (let i = 0; i < 4; i++) px(g, cx + d * (3 + i) * s - (d < 0 ? s : 0), cy + (1 + i) * s, s, s, RED);         // cheek lines
    px(g, cx + d * 3 * s - s, cy - 2 * s, 2 * s, s, K.ink); px(g, cx + d * 3 * s - s, cy - s, 2 * s, s, glow);       // eye + glow
  }
  px(g, cx - 2 * s, cy + 5 * s, 4 * s, 2 * s, RED); px(g, cx - s, cy - 5 * s, 2 * s, 2 * s, K.ink);
}
function lantern(g: G, x: number, y: number) {
  px(g, x + 6, y - 3, 2, 4, K.ink); disc(g, x + 7, y + 6, 6, 7, K.ink); disc(g, x + 7, y + 6, 5, 6, RED);
  px(g, x + 2, y + 2, 10, 1, K.ink); px(g, x + 2, y + 10, 10, 1, K.ink); px(g, x + 4, y + 3, 2, 6, lt(RED, 0.4)); px(g, x + 5, y + 13, 4, 2, K.ink);
}
const neonBar = (g: G, x: number, y: number, w: number, h: number, c: string) => { px(g, x - 1, y - 1, w + 2, h + 2, alpha(c, 0.35)); px(g, x, y, w, h, c); if (w > h) px(g, x, y, w, 1, lt(c, 0.5)); else px(g, x, y, 1, h, lt(c, 0.5)); };

export function paintClub(g: G, R: Rect, t: (tx: number, ty: number) => T, L: LightFn) {
  const C = WORLD.club, B = C.block, Z = C.zone, D = C.dance, S = C.stage, V = C.vip;
  const X = (tx: number) => (tx - R.x) * HT, Y = (ty: number) => (ty - R.y) * HT;
  const wall = (tx: number, ty: number) => t(tx, ty) === T.BUILDING;

  // ------------------------------------------------------------ floor
  for (let ty = Z.y; ty < Z.y + Z.h; ty++) for (let tx = Z.x; tx < Z.x + Z.w; tx++) {
    const x = X(tx), y = Y(ty), v = hash2(tx, ty);
    px(g, x, y, HT, HT, (tx + ty) & 1 ? K.ink : mix(K.ink, K.night, 0.55)); // glossy dark checker
    px(g, x, y, HT, 1, alpha(K.cyan, 0.10)); px(g, x, y, 1, HT, alpha(K.cyan, 0.10));
    if (v < 0.12) px(g, x + 6, y + 6, 4, 4, alpha(K.pink, 0.16)); // spilled neon
    if (inR(V, tx, ty)) { // VIP carpet
      px(g, x, y, HT, HT, K.redDk); for (let j = 0; j < HT; j += 8) for (let i = ((j >> 3) & 1) * 8; i < HT; i += 16) px(g, x + i, y + j, 8, 8, dk(K.red, 0.62));
      px(g, x, y, HT, 1, alpha(GOLD, 0.25));
    }
  }
  // the LED dance floor: framed in neon, 16 px cells in the three neon colours (the live layer lights them up)
  const dx = X(D.x), dy = Y(D.y), dw = D.w * HT, dh = D.h * HT;
  px(g, dx - 6, dy - 6, dw + 12, dh + 12, K.ink);
  for (let j = 0; j < dh; j += 16) for (let i = 0; i < dw; i += 16) {
    const k = Math.floor(hash2(i, j) * 3), c = [K.pink, K.cyan, K.amber][k];
    px(g, dx + i, dy + j, 16, 16, dk(c, 0.82)); px(g, dx + i + 1, dy + j + 1, 14, 14, dk(c, 0.7)); px(g, dx + i + 3, dy + j + 3, 10, 10, dk(c, 0.55));
    px(g, dx + i + 3, dy + j + 3, 10, 2, dk(c, 0.3));
  }
  frame(g, dx - 4, dy - 4, dw + 8, dh + 8, K.pink, 2); frame(g, dx - 7, dy - 7, dw + 14, dh + 14, alpha(K.cyan, 0.7), 1);
  for (const [cx, cy] of [[dx - 4, dy - 4], [dx + dw + 2, dy - 4], [dx - 4, dy + dh + 2], [dx + dw + 2, dy + dh + 2]]) px(g, cx, cy, 4, 4, K.white);
  // gold floor lines leading from the doors toward the dance floor
  for (let i = 0; i < 6; i++) px(g, X(Z.x) + i * 40 + 8, Y(Z.y + 5) + 15, 22, 2, alpha(GOLD, 0.35));

  // ------------------------------------------------------------ wall ring (over the solid tiles)
  for (let ty = B.y; ty < B.y + B.h; ty++) for (let tx = B.x; tx < B.x + B.w; tx++) {
    if (!wall(tx, ty)) continue;
    const x = X(tx), y = Y(ty), north = ty === B.y, south = ty === B.y + B.h - 1, side = tx === B.x || tx === B.x + B.w - 1;
    px(g, x, y, HT, HT, K.ink); px(g, x, y, HT, 4, K.metalHi); px(g, x, y + 4, HT, 2, K.metalLo); // coping
    const c = [K.pink, K.cyan][(tx >> 1) & 1];
    if (north) { // inner face lined with neon tubes and paper lanterns
      px(g, x, y + 6, HT, HT - 6, dk(K.wall, 0.4));
      for (let i = 4; i < HT; i += 8) px(g, x + i, y + 8, 2, 20, alpha(c, 0.8));
      if ((tx & 1) === 0 && !inR(S, tx, ty + 1)) lantern(g, x + 9, y + 12);
      px(g, x, y + HT - 3, HT, 3, K.ink);
    } else if (south) { // the street face: dark glass panels with a lit LED trim
      px(g, x, y + 6, HT, HT - 6, K.glassLo); for (let i = 0; i < HT; i += 16) px(g, x + i, y + 8, 1, 20, alpha(K.glassHi, 0.4));
      px(g, x, y + HT - 8, HT, 2, c); px(g, x, y + HT - 6, HT, 5, K.ink); px(g, x, y + HT - 1, HT, 1, K.curbLo);
    } else if (side) { px(g, x + 6, y, HT - 12, HT, dk(K.wall, 0.3)); px(g, x + 14, y + 2, 2, HT - 4, alpha(c, 0.9)); }
    speckle(g, x, y + 6, HT, HT - 6, K.metalLo, 0.06, 3);
    paintNishiClubWall(g, x, y, north, south);
  }
  // stage screen wall: a 14-tile LED screen on the north wall above the stage, with the club's mask on it
  const sx = X(S.x), sy = Y(B.y) + 6, sw = S.w * HT;
  px(g, sx, sy, sw, 24, K.ink); frame(g, sx, sy, sw, 24, K.cyan, 2);
  for (let j = 3; j < 22; j += 3) px(g, sx + 3, sy + j, sw - 6, 1, alpha(K.pink, 0.12)); // scanlines
  kumadori(g, sx + sw / 2, sy + 11, 1, K.cyan);
  text(g, 'KUMADORI', sx + 18, sy + 3, K.pink, 2); text(g, '2089', sx + sw - 18 - textW('2089', 2), sy + 3, K.cyan, 2);
  L(sx - 10, sy - 6, sw + 20, 44, K.cyan, 0.45, false, true);

  // ------------------------------------------------------------ street face: sign bands and door frames
  const sY = Y(B.y + B.h - 1);
  const sign = (tx0: number, tw: number, label: string, c: string, sc: number) => {
    const x = X(tx0), w = tw * HT;
    px(g, x, sY + 5, w, 19, K.ink); frame(g, x + 1, sY + 6, w - 2, 17, c, 2);
    const lw = textW(label, sc); text(g, label, x + (w - lw) / 2 + 1, sY + 8, K.ink, sc); text(g, label, x + (w - lw) / 2, sY + 7, lt(c, 0.5), sc);
    L(x, sY - 2, w, 32, c, 0.5, true, true);
  };
  sign(215, 8, 'KUMADORI', K.pink, 3);
  sign(230, 11, 'クマドリ 2089', K.cyan, 2);
  sign(207, 4, 'CLUB', K.amber, 2);
  const mx = X(225) + 16; kumadori(g, mx, sY + 14, 0.8, K.pink);
  // door frames: neon posts and a lintel at each opening, a red runner on the threshold
  const doors: Rect[] = [{ x: 210, y: B.y, w: 4, h: 1 }, { x: 211, y: B.y + B.h - 1, w: 4, h: 1 }, { x: 226, y: B.y + B.h - 1, w: 4, h: 1 }, { x: B.x, y: 121, w: 1, h: 3 }];
  for (const d of doors) {
    const x = X(d.x), y = Y(d.y), w = d.w * HT, h = d.h * HT;
    px(g, x, y, w, h, K.ink); // threshold
    if (d.w > d.h) {
      px(g, x + 4, y + 4, w - 8, h - 8, K.redDk); px(g, x + 4, y + 4, w - 8, 2, GOLD); px(g, x + 4, y + h - 6, w - 8, 2, GOLD);
      neonBar(g, x, y, 3, h, K.pink); neonBar(g, x + w - 3, y, 3, h, K.cyan);
      L(x + w / 2 - 20, y - 4, 40, h + 8, d.y === B.y ? K.pink : K.cyan, 0.5, d.y !== B.y);
    } else {
      px(g, x + 4, y + 4, w - 8, h - 8, K.redDk); px(g, x + 4, y + 4, 2, h - 8, GOLD); px(g, x + w - 6, y + 4, 2, h - 8, GOLD);
      neonBar(g, x, y, w, 3, K.pink); neonBar(g, x, y + h - 3, w, 3, K.cyan);
      L(x - 4, y - 4, w + 8, h + 8, K.pink, 0.5);
    }
  }

  // ------------------------------------------------------------ stage, DJ booth, speakers
  const stX = X(S.x), stY = Y(S.y), stW = S.w * HT, stH = S.h * HT;
  shadow(g, stX, stY + stH - 2, stW, 8, 0.45);
  px(g, stX, stY + 6, stW, stH - 22, K.metalLo); // deck top
  for (let i = 0; i < stW; i += 16) px(g, stX + i, stY + 6, 1, stH - 22, alpha(K.ink, 0.5));
  px(g, stX, stY + stH - 16, stW, 16, K.ink); // front face with a strip of LED panels
  for (let i = 0; i < stW; i += 32) { px(g, stX + i + 2, stY + stH - 14, 28, 10, dk([K.pink, K.cyan, K.amber][(i >> 5) % 3], 0.5)); px(g, stX + i + 2, stY + stH - 14, 28, 2, [K.pink, K.cyan, K.amber][(i >> 5) % 3]); }
  frame(g, stX, stY + 4, stW, stH - 4, K.metalHi, 1);
  // DJ booth in the middle: console, two decks, a laptop and the DJ
  const bx = stX + stW / 2 - 56, by = stY + 14;
  px(g, bx, by + 12, 112, 20, K.ink); px(g, bx, by + 12, 112, 3, K.metalHi);
  for (const dxx of [10, 78]) { disc(g, bx + dxx + 12, by + 22, 12, 6, K.metalLo); disc(g, bx + dxx + 12, by + 22, 9, 4, K.ink); disc(g, bx + dxx + 12, by + 22, 2, 1, K.pink); }
  px(g, bx + 44, by + 14, 24, 14, K.metal); for (let i = 0; i < 4; i++) { px(g, bx + 47 + i * 5, by + 16, 2, 8, K.cyan); px(g, bx + 47 + i * 5, by + 20 - (i % 2) * 3, 2, 3, K.white); }
  // the DJ: head and headphones, kimono shoulders, a pink visor
  px(g, bx + 48, by - 12, 16, 22, K.red); px(g, bx + 46, by - 2, 20, 10, K.redLo);
  disc(g, bx + 56, by - 18, 6, 6, K.walk); px(g, bx + 49, by - 21, 14, 2, K.pink); px(g, bx + 49, by - 21, 3, 8, K.ink); px(g, bx + 60, by - 21, 3, 8, K.ink); px(g, bx + 51, by - 17, 10, 3, K.cyan);
  for (const sx2 of [X(S.x - 2), X(S.x + S.w)]) { // speaker stacks flanking the stage
    px(g, sx2 + 1, Y(S.y) + 2, 30, 62, K.ink); frame(g, sx2 + 1, Y(S.y) + 2, 30, 62, K.metalLo, 2);
    for (const cy2 of [18, 44]) { disc(g, sx2 + 16, Y(S.y) + cy2, 10, 8, K.metalLo); disc(g, sx2 + 16, Y(S.y) + cy2, 7, 5, K.ink); disc(g, sx2 + 16, Y(S.y) + cy2, 2, 2, K.amber); }
    px(g, sx2 + 3, Y(S.y) + 60, 26, 2, K.amber);
  }
  L(stX, stY - 4, stW, 70, K.pink, 0.5);

  // ------------------------------------------------------------ bars
  const bar = (b: Rect, label: string, c: string) => {
    const x = X(b.x), y = Y(b.y), w = b.w * HT, h = b.h * HT;
    shadow(g, x, y + h - 4, w, 8, 0.4);
    px(g, x, y + 4, w, h - 4, K.ink); px(g, x + 3, y + 7, w - 6, h - 12, K.metalLo); px(g, x + 3, y + 7, w - 6, 3, K.metalHi); // top
    const vert = h > w;
    if (vert) { neonBar(g, x + w - 4, y + 8, 3, h - 14, c); } else neonBar(g, x + 6, y + h - 6, w - 12, 3, c);
    // bottles standing along the counter: each its own colour, plus a lit shelf of them
    for (let i = 0; i < (vert ? h : w) / 14 - 1; i++) {
      const bc = [K.pink, K.cyan, K.amber, K.leaf][Math.floor(hash2(i, b.x) * 4)], bxx = vert ? x + 10 : x + 8 + i * 14, byy = vert ? y + 10 + i * 14 : y + 9;
      px(g, bxx, byy, 4, 8, dk(bc, 0.2)); px(g, bxx + 1, byy - 3, 2, 4, bc); px(g, bxx, byy, 1, 8, lt(bc, 0.4));
    }
    const lw = textW(label, 2); if (!vert) text(g, label, x + w - lw - 6, y + 12, K.ink, 2), text(g, label, x + w - lw - 6, y + 11, lt(c, 0.5), 2);
    L(x, y, w, h, c, 0.35, false);
  };
  bar({ x: 208, y: 125, w: 2, h: 4 }, 'BAR', K.cyan);
  bar({ x: 230, y: 128, w: 4, h: 1 }, 'SAKE', K.amber);
  for (let j = 0; j < 4; j++) { const sx3 = X(210) + 10, sy3 = Y(125) + 14 + j * 28; disc(g, sx3, sy3, 6, 4, K.ink); disc(g, sx3, sy3 - 1, 5, 3, K.pink); } // stools
  for (let i = 0; i < 4; i++) { const sx3 = X(230) + 14 + i * 34, sy3 = Y(129) + 6; disc(g, sx3, sy3, 6, 4, K.ink); disc(g, sx3, sy3 - 1, 5, 3, K.cyan); }

  // ------------------------------------------------------------ VIP lounge: velvet rope, sofas, a table, gold sign
  for (const r of [{ x: 234, y: 120, w: 1, h: 4 }, { x: 234, y: 126, w: 1, h: 3 }]) {
    const x = X(r.x) + 14, y = Y(r.y), h = r.h * HT;
    px(g, x, y + 6, 4, h - 10, RED); px(g, x + 1, y + 6, 1, h - 10, lt(RED, 0.4)); // the rope
    for (let j = 0; j <= r.h; j++) { const py = y + 6 + (j === r.h ? h - 10 : j * HT - (j ? 4 : 0)); px(g, x - 1, py - 2, 6, 6, K.ink); px(g, x, py - 1, 4, 4, GOLD); } // brass posts
  }
  for (const sy4 of [121, 126]) {
    const x = X(238), y = Y(sy4), w = 2 * HT, h = 2 * HT;
    shadow(g, x, y + h - 4, w, 8, 0.4);
    px(g, x + 2, y + 2, w - 4, h - 4, K.redLo); px(g, x + 2, y + 2, w - 4, 6, RED); px(g, x + 2, y + 2, 8, h - 4, K.redDk); // back and arm
    for (let k = 0; k < 2; k++) { px(g, x + 14, y + 10 + k * 24, w - 18, 20, RED); px(g, x + 14, y + 10 + k * 24, w - 18, 3, lt(RED, 0.3)); px(g, x + 14, y + 29 + k * 24, w - 18, 1, K.ink); }
    L(x, y, w, h, K.pink, 0.4);
  }
  { const x = X(236) + 16, y = Y(124) + 16; // low table with a champagne bucket
    shadow(g, x - 14, y + 6, 30, 6, 0.4); disc(g, x, y, 14, 9, K.ink); disc(g, x, y - 2, 13, 8, K.metalHi); disc(g, x, y - 2, 9, 5, K.metal);
    px(g, x - 3, y - 12, 6, 12, K.glassHi); px(g, x - 2, y - 15, 4, 4, GOLD); px(g, x + 6, y - 8, 4, 8, K.glass); px(g, x - 10, y - 7, 4, 7, K.pink); }
  text(g, 'VIP', X(235) + 12, Y(121) + 6, dk(GOLD, 0.2), 3); text(g, 'VIP', X(235) + 12, Y(121) + 5, GOLD, 3);
  L(X(235), Y(120), 5 * HT, 6 * HT, K.pink, 0.4, false, true);

  // ------------------------------------------------------------ the dance floor's own lights and a few wall lights
  L(dx - 6, dy - 6, dw + 12, dh + 12, K.pink, 0.45); L(dx + dw / 3, dy, dw / 2, dh, K.cyan, 0.4); L(dx + dw / 2, dy + dh / 3, dw / 3, dh / 2, K.amber, 0.25);
}